import { CompletionMetric } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { InfoIcon } from 'components/icons/Info'
import { PercentageIcon } from 'components/icons/Percentage'
import { SparklesIcon } from 'components/icons/Sparkles'
import {
  GradingTooltipAttendance,
  GradingTooltipCameraOnTime,
  GradingTooltipCompletionConfig,
  GradingTooltipCriticalThinking,
  GradingTooltipCustomize,
  GradingTooltipTimeInSession,
  GradingToolTipQuizScore,
} from 'pages/instructor/shared/GradingTooltips'
import {
  useCallback,
  useState,
  useRef,
  forwardRef,
  useMemo,
  useEffect,
  useId,
} from 'react'
import type { ForwardedRef, ReactElement, ReactNode } from 'react'
import { useTranslationTyped } from 'i18n/i18n'

export const ConfigureGradingScalars = forwardRef(
  function ConfigureGradingScalars(
    {
      defaults,
      hasQuizQuestions,
      hasRubrics,
      disableAll,
      onChange,
      completionMetric,
      onCompletionMetricChange,
    }: {
      defaults?: {
        participationScore: number
        quizScore: number
        totalRubricScore: number
        attendanceScore: number
      }
      onChange?: (values: {
        participationScore: number
        quizScore: number
        totalRubricScore: number
        attendanceScore: number
        /**
         * if the values are valid (add up to 100)
         */
        isValid: boolean
        /**
         * sum of all values as an integer
         * valid if sum is 100
         */
        sumAsInt: number
      }) => void
      hasQuizQuestions: boolean
      hasRubrics: boolean
      disableAll?: boolean
      /**
       * How Completion is measured. Absent = legacy time in session.
       * The metric toggle renders only when onCompletionMetricChange is provided.
       */
      completionMetric?: CompletionMetric
      onCompletionMetricChange?: (metric: CompletionMetric) => void
    },
    argsRef?: ForwardedRef<typeof defaults>
  ) {
    //
    const mutableArgsRef = argsRef && 'current' in argsRef ? argsRef : undefined

    const initialValuesFromRef =
      mutableArgsRef?.current && convertToIntegers(mutableArgsRef?.current)

    const [values, setValues] = useState(
      initialValuesFromRef ||
        (defaultsValid({ defaults, hasQuizQuestions, hasRubrics })
          ? convertToIntegers(defaults!)
          : generateDefaults({ hasQuizQuestions, hasRubrics }))
    )

    const disabled = useMemo(
      () => getDisabled({ hasQuizQuestions, hasRubrics }),
      [hasQuizQuestions, hasRubrics]
    )

    const { tt } = useTranslationTyped()

    const localRef = useRef<typeof values>(values)
    const refToUse = argsRef ?? localRef

    // focus first input on mount if form is not disabled
    const firstInputRef = useRef<HTMLInputElement>(null)

    const isCameraMetric = completionMetric === CompletionMetric.cameraOnTime
    // Start expanded when the non-default (camera) metric is already selected so
    // the choice is visible; otherwise collapsed behind the "Advanced" link.
    const [metricExpanded, setMetricExpanded] = useState(isCameraMetric)

    const textInputOnChange = useCallback(
      (k: keyof typeof values, value: string) => {
        const parsed = value ? Number.parseInt(value) : 0
        if (parsed < 0 || parsed > 100) return
        setValues((prevScalars) => ({
          ...prevScalars,
          [k]: isNaN(parsed) ? 0 : parsed,
        }))
      },
      []
    )

    // update ref on scalars change and fire on change event if provided
    useEffect(() => {
      const newScalarFloats = convertToFloats(values)
      if (onChange) {
        const sumAsInt = Object.values(values).reduce((a, b) => a + b, 0)
        const isValid = sumAsInt === 100

        onChange({
          ...newScalarFloats,
          sumAsInt,
          isValid,
        })
      }
      if (!('current' in refToUse)) return
      refToUse.current = convertToFloats(values)
    }, [onChange, refToUse, values])

    return (
      <div
        className="flex w-full flex-col gap-3"
        data-testid="configure-grading"
      >
        {hasQuizQuestions && (
          <GradingTextInput
            testId="grading-quiz-score"
            label={tt.instructor_library.quiz_scores()}
            labelDescription={tt.instructor_library.quiz_scores_description()}
            value={values.quizScore.toString()}
            disabled={disabled.has('quizScore') || disableAll}
            onChange={(e) => textInputOnChange('quizScore', e.target.value)}
            ref={firstInputRef}
            extra={
              <BreakoutTooltip content={<GradingToolTipQuizScore />}>
                <div>
                  <InfoIcon
                    size={15}
                    className="fill-on-surface-var"
                    strokeWidth={1.25}
                  />
                </div>
              </BreakoutTooltip>
            }
          />
        )}
        <GradingTextInput
          testId="grading-attendance-score"
          label={tt.instructor_library.attendance()}
          labelDescription={tt.instructor_library.attendance_description()}
          disabled={disableAll}
          value={values.attendanceScore.toString()}
          onChange={(e) => textInputOnChange('attendanceScore', e.target.value)}
          // this is first input if no quiz questions
          ref={hasQuizQuestions ? undefined : firstInputRef}
          extra={
            <BreakoutTooltip
              className="max-w-[350px]"
              content={<GradingTooltipAttendance />}
            >
              <div>
                <InfoIcon
                  size={15}
                  className="fill-on-surface-var"
                  strokeWidth={1.25}
                />
              </div>
            </BreakoutTooltip>
          }
        />

        <GradingTextInput
          testId="grading-participation-score"
          label={tt.instructor_library.participation()}
          labelDescription={
            isCameraMetric
              ? tt.instructor_library.participation_description_camera()
              : tt.instructor_library.participation_description()
          }
          disabled={disableAll}
          value={values.participationScore.toString()}
          onChange={(e) =>
            textInputOnChange('participationScore', e.target.value)
          }
          extra={
            <BreakoutTooltip content={<GradingTooltipCompletionConfig />}>
              <div>
                <InfoIcon
                  size={15}
                  className="fill-on-surface-var"
                  strokeWidth={1.25}
                />
              </div>
            </BreakoutTooltip>
          }
          belowDescription={
            onCompletionMetricChange ? (
              <CompletionMetricControl
                metric={completionMetric ?? CompletionMetric.timeInSession}
                onChange={onCompletionMetricChange}
                expanded={metricExpanded}
                setExpanded={setMetricExpanded}
                disabled={disableAll}
              />
            ) : undefined
          }
        />
        {hasRubrics && (
          <GradingTextInput
            testId="grading-critical-thinking-score"
            label={tt.instructor_library.critical_thinking()}
            labelDescription={tt.instructor_library.critical_thinking_description()}
            value={values.totalRubricScore.toString()}
            disabled={disabled.has('totalRubricScore') || disableAll}
            onChange={(e) =>
              textInputOnChange('totalRubricScore', e.target.value)
            }
            extra={
              <div className="flex flex-row items-center gap-1 pl-1 text-label-small">
                <SparklesIcon className="stroke-fixed-accent-color" size={15} />
                <BreakoutTooltip content={<GradingTooltipCriticalThinking />}>
                  <div>
                    <InfoIcon
                      size={15}
                      className="fill-on-surface-var"
                      strokeWidth={1.25}
                    />
                  </div>
                </BreakoutTooltip>
              </div>
            }
          />
        )}
      </div>
    )
  }
)

const GradingTextInput = forwardRef(function GradingTextInput(
  {
    testId,
    label,
    labelDescription,
    value,
    onChange,
    disabled,
    extra,
    belowDescription,
  }: {
    testId?: string
    label: string
    labelDescription: string
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabled?: boolean
    extra?: ReactNode
    belowDescription?: ReactNode
  },
  ref: ForwardedRef<HTMLInputElement>
) {
  const labelId = useId()
  const descriptionId = useId()
  return (
    <div className="flex flex-row justify-between gap-2">
      <style>
        {/* disable spin buttons on number inputs*/}
        {`
          input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type='number'] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      <div className="flex flex-col">
        {!extra && (
          <strong id={labelId} className="text-label-medium">
            {label}
          </strong>
        )}
        {extra && (
          <div className="flex flex-row gap-1">
            <strong id={labelId} className="text-label-medium">
              {label}
            </strong>
            {extra}
          </div>
        )}
        <strong
          id={descriptionId}
          className="text-body-medium text-on-surface-var"
        >
          {labelDescription}
        </strong>
        {belowDescription && <div className="mt-1">{belowDescription}</div>}
      </div>
      <BreakoutTextInput
        testId={testId}
        kind="secondary"
        variant="compact"
        className="w-15 shrink-0"
        value={value}
        disabled={disabled}
        onChange={onChange}
        type="number"
        min={0}
        max={100}
        TrailingIcon={PercentageIcon}
        iconSize={10}
        iconClassName={'text-on-surface-var right-[15px]!'}
        inputClassName="text-right pr-[26px]! pl-2.5"
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        ref={ref}
      />
    </div>
  )
})

function MetricRadioLabel({
  text,
  tooltip,
  htmlFor,
  disabled,
}: {
  text: string
  tooltip: ReactElement
  /** id of the radio this label names and activates (native label association). */
  htmlFor: string
  disabled?: boolean
}) {
  return (
    <div className="flex flex-row items-center gap-1 text-label-medium">
      {/* A real <label htmlFor> references the radio by id rather than wrapping
          it, so clicking the text natively activates the radio (firing the
          group's onValueChange) and gives the radio its accessible name, without
          the label ever receiving the synthetic bubble-<input> clicks radix
          dispatches on controlled value changes. */}
      <label
        htmlFor={htmlFor}
        className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
      >
        {text}
      </label>
      <BreakoutTooltip content={tooltip}>
        <div>
          <InfoIcon
            size={15}
            className="fill-on-surface-var"
            strokeWidth={1.25}
          />
        </div>
      </BreakoutTooltip>
    </div>
  )
}

/**
 * The Completion measurement toggle rendered under the Completion row. Collapsed
 * it shows an inline link; expanded it shows the two metric radios. The selected
 * metric is a controlled value owned by the host (kept out of the scalar sum).
 */
function CompletionMetricControl({
  metric,
  onChange,
  expanded,
  setExpanded,
  disabled,
}: {
  metric: CompletionMetric
  onChange: (metric: CompletionMetric) => void
  expanded: boolean
  setExpanded: (expanded: boolean) => void
  disabled?: boolean
}) {
  const { tt } = useTranslationTyped()
  const isCamera = metric === CompletionMetric.cameraOnTime

  if (!expanded) {
    return (
      <BreakoutTooltip content={<GradingTooltipCustomize />}>
        <button
          type="button"
          disabled={disabled}
          data-testid="completion-metric-expand"
          onClick={() => setExpanded(true)}
          className="self-start text-left text-label-medium text-on-surface-var underline disabled:opacity-50"
        >
          {isCamera
            ? tt.instructor_library.completion_customize()
            : tt.instructor_library.completion_customize_advanced()}
        </button>
      </BreakoutTooltip>
    )
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="completion-metric-control"
    >
      <div className="flex flex-row items-center justify-between">
        <strong className="text-label-medium text-on-surface-var">
          {tt.instructor_library.completion_measure_using()}
        </strong>
        <button
          type="button"
          disabled={disabled}
          data-testid="completion-metric-collapse"
          onClick={() => setExpanded(false)}
          className="text-label-medium text-on-surface-var underline disabled:opacity-50"
        >
          {tt.instructor_library.completion_collapse()}
        </button>
      </div>
      <BreakoutRadioGroup
        value={isCamera ? 'camera' : 'time'}
        disabled={disabled}
        // Both the bullet and, via the label's htmlFor, the label text activate
        // the radio, so this is the single source of truth for selection.
        onValueChange={(value) =>
          onChange(
            value === 'camera'
              ? CompletionMetric.cameraOnTime
              : CompletionMetric.timeInSession
          )
        }
        className="flex flex-col gap-2 pl-1"
      >
        {(
          [
            {
              value: 'time',
              text: tt.instructor_library.completion_metric_time(),
              tooltip: <GradingTooltipTimeInSession />,
            },
            {
              value: 'camera',
              text: tt.instructor_library.completion_metric_camera(),
              tooltip: <GradingTooltipCameraOnTime />,
            },
          ] as const
        ).map((opt) => {
          const radioId = `completion-metric-${opt.value}-radio`
          return (
            // `bare` renders only the radio bullet; the sibling label is tied to
            // it by htmlFor so it both names and activates the radio. The label
            // is not an ancestor of radix's hidden input, so it never catches
            // the synthetic bubble clicks a wrapping element would.
            <div key={opt.value} className="flex flex-row items-center gap-2">
              <BreakoutRadioButton
                variant="bare"
                id={radioId}
                value={opt.value}
                label={null}
                disabled={disabled}
                data-testid={`completion-metric-${opt.value}`}
              />
              <MetricRadioLabel
                text={opt.text}
                tooltip={opt.tooltip}
                htmlFor={radioId}
                disabled={disabled}
              />
            </div>
          )
        })}
      </BreakoutRadioGroup>
    </div>
  )
}

function convertToIntegers<T extends Record<string, number>>(values: T): T {
  // if any val is float multiply all by 100
  return Object.fromEntries(
    Object.entries(values).map(([key, val]) => [key, Math.round(val * 100)])
  ) as T
}

function convertToFloats<T extends Record<string, number>>(values: T): T {
  return Object.fromEntries(
    Object.entries(values).map(([key, val]) => [key, val / 100])
  ) as T
}

function defaultsValid({
  defaults,
  hasQuizQuestions,
  hasRubrics,
}: {
  defaults?: {
    attendanceScore: number
    participationScore: number
    quizScore: number
    totalRubricScore: number
  }
  hasQuizQuestions: boolean
  hasRubrics: boolean
}) {
  if (!defaults) return false
  const { quizScore, totalRubricScore, participationScore, attendanceScore } =
    defaults
  if (!hasQuizQuestions && defaults.quizScore !== 0) return false
  if (!hasRubrics && defaults.totalRubricScore !== 0) return false
  if (
    quizScore < 0 ||
    participationScore < 0 ||
    totalRubricScore < 0 ||
    attendanceScore < 0
  )
    return false

  if (
    quizScore + participationScore + totalRubricScore + attendanceScore - 1 >
    1e-10 // to account for floating point errors
  )
    return false
  return true
}

function getDisabled({
  hasQuizQuestions,
  hasRubrics,
}: {
  hasQuizQuestions: boolean
  hasRubrics: boolean
}): Set<'quizScore' | 'totalRubricScore'> {
  const set = new Set<'totalRubricScore' | 'quizScore'>()
  if (!hasQuizQuestions) set.add('quizScore')
  if (!hasRubrics) set.add('totalRubricScore')
  return set
}

function generateDefaults({
  hasQuizQuestions,
  hasRubrics,
}: {
  hasQuizQuestions: boolean
  hasRubrics: boolean
}) {
  if (hasQuizQuestions && hasRubrics)
    return {
      quizScore: 40,
      participationScore: 15,
      totalRubricScore: 30,
      attendanceScore: 15,
    }

  // no quiz no rubric
  if (!hasQuizQuestions && !hasRubrics) {
    return {
      participationScore: 50,
      totalRubricScore: 0,
      quizScore: 0,
      attendanceScore: 50,
    }
  }

  // no quiz
  if (!hasQuizQuestions) {
    return {
      participationScore: 15,
      totalRubricScore: 70,
      quizScore: 0,
      attendanceScore: 15,
    }
  }

  // no rubrics
  return {
    participationScore: 15,
    quizScore: 70,
    attendanceScore: 15,
    totalRubricScore: 0,
  }
}
